/*
 * Login screen — email/password against the real prod API.
 *
 * Apple Sign-In on web is a separate flow (Sign In with Apple JS / form_post
 * redirect). Deferred to a future pass — the mobile app's "expo-apple-
 * authentication" doesn't apply here.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { BrandWordmark } from '@/components/BrandWordmark';

interface LocationState {
  from?: { pathname?: string };
  notice?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One-shot success notice (e.g. "Password updated — sign in…") passed in
  // via location.state from the password-reset flow.
  const [notice, setNotice] = useState<string | null>(
    (location.state as LocationState | null)?.notice ?? null,
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!username.trim() || !password) {
      setError('Please enter your username or email and your password.');
      return;
    }

    setLoading(true);
    setNotice(null);
    try {
      const res = await api.login(username.trim(), password);
      login(
        res.userId,
        res.username,
        res.displayName,
        res.accessToken,
        res.refreshToken,
        stayLoggedIn,
      );
      // If we got here from an invite link the URL carries ?join=CODE
      // plus optionally claim+token. Route them back to JoinTournament
      // with those preserved so the user can confirm the personal claim.
      const search = new URLSearchParams(window.location.search);
      const joinCode = search.get('join');
      if (joinCode) {
        const sp = new URLSearchParams();
        const claim = search.get('claim');
        const token = search.get('token');
        if (claim) sp.set('claim', claim);
        if (token) sp.set('token', token);
        const qs = sp.toString();
        navigate(`/app/join/${joinCode}${qs ? `?${qs}` : ''}`, { replace: true });
        return;
      }
      // Check for a personal event invitation before continuing. The pending
      // screen only links a roster slot after an emailed one-time code.
      const dest =
        (location.state as LocationState | null)?.from?.pathname ?? '/app/home';
      const pendingParams = new URLSearchParams({ next: dest });
      navigate(`/app/invitations?${pendingParams.toString()}`, { replace: true });
    } catch (err: any) {
      setError(err.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Link to="/" className="auth-brand">
          <BrandWordmark className="brand-wordmark" />
        </Link>

        <h1 className="auth-title">Welcome back</h1>
        {new URLSearchParams(window.location.search).get('join') ? (
          <p className="auth-sub">
            Sign in to join the event you were invited to.
          </p>
        ) : (
          <p className="auth-sub">Sign in to score the friendly games.</p>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <label className="auth-label">
            <span>USERNAME OR EMAIL</span>
            <input
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              className="auth-input"
              disabled={loading}
            />
          </label>

          <label className="auth-label">
            <span>PASSWORD</span>
            <div className="auth-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="auth-input auth-input-pad-right"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="auth-eye-btn"
                tabIndex={-1}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </label>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              // Wrap on the narrowest phones — checkbox label + forgot
              // link total ~235px and the smallest cards give ~216px.
              flexWrap: 'wrap',
            }}
          >
            <label className="auth-checkbox" style={{ marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={stayLoggedIn}
                onChange={(e) => setStayLoggedIn(e.target.checked)}
              />
              <span>Stay signed in</span>
            </label>
            <Link
              to="/app/forgot-password"
              className="auth-footer-link"
              style={{ fontSize: 13 }}
            >
              Forgot password?
            </Link>
          </div>

          {notice && (
            <div
              className="auth-error"
              style={{
                background: 'rgba(34, 197, 94, 0.12)',
                borderColor: 'rgba(34, 197, 94, 0.45)',
                color: '#22c55e',
              }}
            >
              {notice}
            </div>
          )}
          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-cta" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in →'}
          </button>

          <div className="auth-footer">
            <span>Don't have an account?</span>{' '}
            <Link
              to={`/app/register${window.location.search}`}
              className="auth-footer-link"
            >
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
